import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ClipboardCheck, X, Loader2, Check } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import OpeningHoursPicker, { normalizeOpeningHours, type OpeningHours } from '@/src/components/onboarding/opening-hours-picker';
import NearbyTransportPicker, { type NearbyStop } from '@/src/components/dashboard/widgets/nearby-transport-picker';
import AddressAutocomplete from '@/src/components/onboarding/address-autocomplete';
import GoogleListingPicker from '@/src/components/dashboard/widgets/google-listing-picker';
import CuisineField from '@/src/components/cuisine-field';

export interface VenueSetupFields {
  cuisine: string | null;
  address: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  phone: string | null;
  email: string | null;
  website_url: string | null;
  opening_hours: OpeningHours | null;
  nearby_stops: NearbyStop[] | null;
  latitude: number | null;
  longitude: number | null;
}

interface FinishVenueSetupProps {
  venueId: string;
  venueName: string;
  fields: VenueSetupFields;
  onSaved: () => void;
  onDismiss: () => void;
  startOpen?: boolean;
  /** Popup only (e.g. opened from Settings): closing it calls onDismiss
   *  instead of collapsing into the dashboard banner. */
  modalOnly?: boolean;
}

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

function isLocationDone(f: VenueSetupFields) {
  return !!(f.address && f.suburb && f.state && f.postcode);
}
function isHoursDone(f: VenueSetupFields) {
  return !!f.opening_hours;
}

export default function FinishVenueSetup({ venueId, venueName, fields, onSaved, onDismiss, startOpen, modalOnly }: FinishVenueSetupProps) {
  const c = useAppColors();
  const [open, setOpen] = useState(startOpen ?? modalOnly ?? false);
  const [saving, setSaving] = useState(false);

  const [address, setAddress] = useState(fields.address ?? '');
  const [suburb, setSuburb] = useState(fields.suburb ?? '');
  const [state, setState] = useState(fields.state ?? '');
  const [postcode, setPostcode] = useState(fields.postcode ?? '');
  const [phone, setPhone] = useState(fields.phone ?? '');
  const [email, setEmail] = useState(fields.email ?? '');
  const [website, setWebsite] = useState(fields.website_url ?? '');
  const [hours, setHours] = useState<OpeningHours>(normalizeOpeningHours(fields.opening_hours));
  const [cuisine, setCuisine] = useState(fields.cuisine ?? '');
  const [nearbyStops, setNearbyStops] = useState<NearbyStop[]>((fields.nearby_stops as NearbyStop[] | null) ?? []);
  const [selectedCoords, setSelectedCoords] = useState<{ latitude: number; longitude: number } | null>(null);

  // Website is optional for many venues, so it's not part of "setup complete".
  const checklist = useMemo(() => ([
    { label: 'Cuisine', done: !!fields.cuisine },
    { label: 'Location', done: isLocationDone(fields) },
    { label: 'Phone', done: !!fields.phone },
    { label: 'Opening hours', done: isHoursDone(fields) },
  ]), [fields]);
  const doneCount = checklist.filter((c) => c.done).length;

  // Close the popup: from Settings this ends it completely; on the dashboard
  // it collapses back into the banner as before.
  function close() {
    if (modalOnly) { onDismiss(); return; }
    setOpen(false);
  }

  async function save() {
    setSaving(true);
    await supabase.from('venues').update({
      cuisine: cuisine.trim() || null,
      address: address.trim() || null,
      suburb: suburb.trim() || null,
      state: state.trim() || null,
      postcode: postcode.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      website_url: website.trim() || null,
      opening_hours: hours,
      nearby_stops: nearbyStops,
      ...(selectedCoords ?? {}),
    }).eq('id', venueId);
    setSaving(false);
    if (!modalOnly) setOpen(false);
    onSaved();
  }

  if (!open) {
    if (modalOnly) return null;
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer"
        style={{ background: c.accentBg, border: `1px solid ${c.accentBorder}` }}
        onClick={() => setOpen(true)}
      >
        <ClipboardCheck size={16} style={{ color: c.accentSoft }} className="flex-shrink-0" />
        <span className="text-sm flex-1" style={{ color: c.text }}>
          Finish setting up {venueName} — {doneCount} of {checklist.length} done
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="flex-shrink-0 p-1"
          style={{ color: c.textFaint }}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return createPortal(
    <AnimatePresence>
      <div className="kb-aware fixed inset-0 z-[500] flex items-center justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={close}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md rounded-2xl p-5 my-8 max-h-[85vh] overflow-y-auto"
          style={{ background: c.bg, border: `1px solid ${c.border}` }}
        >
          <h3 className="text-base font-semibold mb-1" style={{ color: c.text }}>Finish setting up {venueName}</h3>
          <p className="text-xs mb-4" style={{ color: c.textFaint }}>This helps OpsAlo give more useful, location-aware answers. Everything here is optional and saves independently.</p>

          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1.5 block" style={{ color: c.textFaint }}>Cuisine</label>
              <CuisineField value={cuisine} onChange={setCuisine} selectClassName="w-full h-11 rounded-lg px-3 text-sm outline-none" style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }} />
            </div>
            <AddressAutocomplete
              defaultValue={fields.address ?? ''}
              onSelect={async (result) => {
                setAddress(result.address);
                setSuburb(result.suburb);
                setState(result.state);
                setPostcode(result.postcode);
                setSelectedCoords({ latitude: result.latitude, longitude: result.longitude });

                // Auto-find nearby stations right away — the owner can still add/remove
                // manually below, this just saves them the extra search step.
                const { data } = await supabase.functions.invoke('stop-finder', {
                  body: { lat: result.latitude, lon: result.longitude },
                });
                if (data?.ok && data.results?.length > 0) {
                  setNearbyStops(data.results as NearbyStop[]);
                }
              }}
            />

            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1.5 block" style={{ color: c.textFaint }}>Contact</label>
              <div className="space-y-2">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" inputMode="tel" className="w-full h-11 rounded-lg px-3 text-sm outline-none" style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }} />
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="w-full h-11 rounded-lg px-3 text-sm outline-none" style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }} />
                <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="Website URL (optional)" type="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="w-full h-11 rounded-lg px-3 text-sm outline-none" style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }} />
                <NearbyTransportPicker value={nearbyStops} onChange={setNearbyStops} />
              </div>
            </div>

            <GoogleListingPicker
              venueId={venueId}
              defaultQuery={venueName}
              onConfirmed={() => {}}
            />

            <OpeningHoursPicker value={hours} onChange={setHours} />
          </div>

          <div className="flex gap-2 mt-5">
            <button onClick={close} className="flex-1 py-2.5 rounded-lg text-sm font-medium" style={{ background: c.iconBg, color: c.text }}>
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: c.accentBg, color: c.accentSoft, border: `1px solid ${c.accentBorder}` }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : (<><Check size={14} /> Save</>)}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}